/**
 * @fullvsl/backend/aggregation — analytics aggregation (FUL-5).
 *
 * Pure functions over `AnalyticsEvent[]`. The dashboard/API read paths call these;
 * keeping them pure makes the core conversion/retention math unit-testable without
 * a store or DB. All metrics use MAX-watched progress (anti-gaming, matches the
 * contracts CTA `basedOn: "maxWatched"` default).
 *
 * Metrics (contracts §9 / PRD §10):
 *  - playRate      = plays / impressions
 *  - ctaClickRate  = cta_clicked / cta_shown  (conversion proxy)
 *  - avgWatchPct   = mean per-visitor max watched percent
 *  - retentionCurve= viewers reaching each decile (0,10,…,100)
 */
import type {
  AnalyticsBreakdown,
  AnalyticsEvent,
  AnalyticsOverview,
  ID,
  ISODate,
} from "../../contracts/src/contracts";
import {
  computeRetentionByBuckets,
  conversionProxyRate,
  RETENTION_DECILES,
} from "../../contracts/src/analytics-schema";

export type EventTypeFilter = AnalyticsEvent["type"];

/** Events of a given type (or all when omitted). */
export function byType(events: AnalyticsEvent[], type?: EventTypeFilter): AnalyticsEvent[] {
  return type ? events.filter((e) => e.type === type) : events;
}

/** Distinct visitor ids among events of the given type. */
export function uniqueVisitors(events: AnalyticsEvent[], type?: EventTypeFilter): string[] {
  const set = new Set<string>();
  for (const e of byType(events, type)) if (e.visitorId) set.add(e.visitorId);
  return [...set];
}

/**
 * Per-visitor MAX watched percent. Contributions:
 *  - milestone events → meta.milestone (25/50/75/100)
 *  - completed events → 100
 *  - any event with meta.pct → that pct
 *  - events with no pct signal are ignored
 * Returns map visitorId → maxPct (0..100).
 */
export function maxPctByVisitor(events: AnalyticsEvent[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of events) {
    let pct: number | undefined;
    if (e.type === "milestone" && e.meta?.milestone != null) {
      pct = e.meta.milestone;
    } else if (e.type === "completed") {
      pct = 100;
    } else if (typeof e.meta?.pct === "number") {
      pct = e.meta.pct;
    }
    if (pct == null) continue;
    const prev = out.get(e.visitorId);
    if (prev == null || pct > prev) out.set(e.visitorId, pct);
  }
  return out;
}

/** Round to 2 decimals (matches the contracts `pct` 2dp convention). */
function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function mean(nums: number[]): number {
  if (nums.length === 0) return 0;
  let s = 0;
  for (const n of nums) s += n;
  return s / nums.length;
}

/**
 * Compute the analytics overview for a VSL's event stream.
 * `windowStart/windowEnd` bound the curve; defaults to the min/max clientTs.
 */
export function computeOverview(
  events: AnalyticsEvent[],
  vslId: ID,
  window?: { from?: ISODate; to?: ISODate },
): AnalyticsOverview {
  const impressions = byType(events, "impression").length;
  const plays = byType(events, "play").length;
  const playRate = impressions > 0 ? round2(plays / impressions) : 0;

  const ctaShown = byType(events, "cta_shown").length;
  const ctaClicked = byType(events, "cta_clicked").length;
  // Conversion proxy (FUL-6 §D): cta_clicked / cta_shown.
  const ctaClickRate = conversionProxyRate(ctaShown, ctaClicked);

  const resumeOffered = byType(events, "resume_offered").length;
  const resumeAccepted = byType(events, "resume_accepted").length;

  // Retention + avg watch are over max-watched pct per visitor.
  const pctByVisitor = maxPctByVisitor(events);
  const viewerPcts = [...pctByVisitor.values()];
  const avgWatchPct = round2(mean(viewerPcts));

  // Retention (FUL-6 §C): max-watched bucketed on the decile grid. Visitors
  // with no progress signal are excluded (they have no max-watched entry); the
  // overview `retentionCurve` field name (`retained`) maps from `reached`.
  const buckets = computeRetentionByBuckets(pctByVisitor, { grid: RETENTION_DECILES });
  const retentionCurve = buckets.map((b) => ({ pct: b.pct, retained: b.reached }));

  let windowStart: ISODate;
  let windowEnd: ISODate;
  if (window?.from && window.to) {
    windowStart = window.from;
    windowEnd = window.to;
  } else {
    const ts = events.map((e) => e.clientTs).sort();
    windowStart = ts[0] ?? window?.from ?? new Date(0).toISOString();
    windowEnd = ts[ts.length - 1] ?? window?.to ?? new Date().toISOString();
  }

  return {
    vslId,
    windowStart,
    windowEnd,
    impressions,
    plays,
    playRate,
    avgWatchPct,
    retentionCurve,
    ctaShown,
    ctaClicked,
    ctaClickRate,
    resumeOffered,
    resumeAccepted,
  };
}

/**
 * Compute source/device/aspect-ratio breakdowns (PRD §10.3 source & device
 * breakdowns). Each visitor is attributed to exactly ONE bucket per dimension —
 * their first-seen value in clientTs order — so the rows sum to the total unique
 * viewer count and no visitor is double-counted across buckets. This makes the
 * breakdown compositional with the overview's viewer totals.
 */
export function computeBreakdown(events: AnalyticsEvent[], vslId: ID): AnalyticsBreakdown {
  // First-seen value per visitor per dimension (stable by clientTs, not arrival).
  const ordered = [...events].sort((a, b) => (a.clientTs < b.clientTs ? -1 : a.clientTs > b.clientTs ? 1 : 0));
  const sourceByVisitor = new Map<string, string>();
  const deviceByVisitor = new Map<string, string>();
  const aspectByVisitor = new Map<string, string>();

  for (const e of ordered) {
    if (e.meta?.source != null && !sourceByVisitor.has(e.visitorId)) {
      sourceByVisitor.set(e.visitorId, e.meta.source);
    }
    if (e.meta?.device != null && !deviceByVisitor.has(e.visitorId)) {
      deviceByVisitor.set(e.visitorId, e.meta.device);
    }
    if (e.meta?.aspectRatio != null && !aspectByVisitor.has(e.visitorId)) {
      aspectByVisitor.set(e.visitorId, e.meta.aspectRatio);
    }
  }

  const tally = (assigned: Map<string, string>): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const v of assigned.values()) out[v] = (out[v] ?? 0) + 1;
    return out;
  };

  return {
    vslId,
    bySource: tally(sourceByVisitor),
    byDevice: tally(deviceByVisitor),
    byAspectRatio: tally(aspectByVisitor),
  };
}
